import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useReactFlow } from "reactflow";
import { ClipboardCopyIcon } from "@heroicons/react/outline";
import { getIn, useFormikContext } from "formik";
import { FlowStepData } from "@hilos/types/flow";
import { FlowData } from "@hilos/types/flow";
import useFlowClipboardStore from "../hooks/useFlowClipboardStore";

interface NodeContextMenuProps {
  nodeId: string;
  top: number;
  left: number;
  onClose: () => void;
}

function NodeContextMenu({ nodeId, top, left, onClose }: NodeContextMenuProps) {
  const [t] = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const { getNode } = useReactFlow();
  const { values } = useFormikContext<FlowData>();
  const { setCopiedStep } = useFlowClipboardStore();

  const node = getNode(nodeId);
  const stepIndex = node?.data?.index;
  const stepType = node?.data?.type;

  const handleCopy = useCallback(() => {
    if (node && typeof stepIndex === "number" && stepType !== "trigger") {
      const step = getIn(values, `steps.${stepIndex}`) as FlowStepData;

      if (step) {
        const cleanStep: FlowStepData = {
          ...step,
          next_step_default: null,
          next_step_alternate: null,
          answer_failed_next_step: null,
          next_steps_for_options: [],
        };

        setCopiedStep(cleanStep);
        onClose();
      }
    }
  }, [node, stepIndex, stepType, values, setCopiedStep, onClose]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("click", handleClickOutside);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!node || node.type === "trigger" || stepType === "trigger") return null;

  return createPortal(
    <div
      ref={menuRef}
      className="absolute z-50 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden"
      style={{ top, left }}
    >
      <div className="py-1">
        <button
          type="button"
          className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          onClick={handleCopy}
        >
          <ClipboardCopyIcon
            className="mr-3 h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
          {t("flows:context-menu.copy", "Copy step")}
        </button>
      </div>
    </div>,
    document.body
  );
}

export default NodeContextMenu;
